import React, {
    forwardRef,
    Ref,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { Button, Form, IRuleDescriptor, Message, DoubleLeftOutlined } from '@fx-ui/fine-design';
import { ErrorInput, DataType, DeptItem } from '@fx-ui/jdy-design-biz';
import { ContactValuePanel, DeptSelectDialog } from '~/components';
import {
    assign,
    forEach,
    get,
    getApi,
    getErrorMsg,
    getMemberFieldCommitValue,
    getMemberFieldRule,
    hasFunctionContactPhone,
    includes,
    isEmpty,
    isObjectEmpty,
    join,
    map,
    openInNewTab,
    post,
    size,
    tracker,
    trim,
    validateMemberFieldValue,
    verifyEmail,
    verifyPhone
} from '~/utils';
import Intl from '@fx-ui/intl';
import { API, CONTACTS, ECode, TEXT_TYPE_REGEXP, TRACKER, UGC_MAX_LENGTH } from '~/constants';
import {
    IDept,
    MemberFieldValueType,
    MemberType,
    ResignedMemberRecoveryInfo,
    ValueOf
} from '~/interfaces';
import { I18nPhone, getContactDeptList, getCorpContactUrl } from '@fx-ui/platform-sdk';
import { useStores } from '~/hooks';
import {
    MemberFieldDatetime,
    MemberFieldDept,
    MemberFieldNumber,
    MemberFieldSelect,
    MemberFieldUser
} from '~/container/member-field-value';

interface IAddMemberPanelProps {
    currentDeptInfo?: IDept;
    disabledDeptIds?: string[];
    resignedMember?: ResignedMemberRecoveryInfo;
    onInviteMember: () => void;
}

const { MEMBER_NAME, MEMBER_ID } = UGC_MAX_LENGTH.CONTACTS;
const { EMAIL } = UGC_MAX_LENGTH.USER;

const MANUALLY_INVITE_FIELDS = window.jdy_config.member_invite.add_tab_display_fields;
type InviteField = ValueOf<Pick<(typeof MANUALLY_INVITE_FIELDS)[number], 'field'>>;

/**
 * 获取邀请成员-手动添加成员表单字段是否必填规则
 */
const getFieldRequiredRules = () => {
    const rules: Record<InviteField, boolean> = {
        nickname: false,
        phone: false,
        email: false,
        username: false,
        dept: false
    };
    forEach(MANUALLY_INVITE_FIELDS, (field) => {
        assign(rules, {
            [field.field]: field.required
        });
    });

    return rules;
};

const FIELD_REQUIRED_RULES = getFieldRequiredRules();

export interface AddMemberPanelRef {
    invite: () => Promise<void> | void;
    clear: () => void;
}

/**
 * 邀请成员-手动添加成员
 * @param props
 */
const AddMemberPanel = forwardRef((props: IAddMemberPanelProps, ref: Ref<AddMemberPanelRef>) => {
    const { currentDeptInfo, disabledDeptIds, resignedMember, onInviteMember } = props;

    const [deptItems, setDeptItems] = useState<DeptItem[] | undefined>(
        currentDeptInfo ? [currentDeptInfo] : undefined
    );
    const [deptSelectVisible, setDeptSelectVisible] = useState<boolean>(false);
    // 是否为离职成员恢复在职场景
    const isRecoverResignedMember = !isEmpty(resignedMember);

    const formRef = useRef<any>();
    const moreFormRef = useRef<any>();
    const [refreshToken, setRefreshToken] = useState(1);
    const [showMore, setShowMore] = useState(isRecoverResignedMember);
    const moreContainer = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!isRecoverResignedMember && showMore && moreContainer.current) {
            moreContainer.current.scrollIntoView({
                block: 'start',
                inline: 'nearest',
                behavior: 'smooth'
            });
        }
    }, [showMore]);

    useImperativeHandle(ref, () => {
        return {
            invite: () => {
                tracker(TRACKER.MEMBER_INVITE_OF_PHONE_EMAIL);
                return checkMemberInfo();
            },
            clear: () => clearFormValue('clear')
        };
    });

    const { corpUserStore, memberFieldStore } = useStores();

    const [defaultValueMap, emptyValueMap, moreFormRules] = useMemo(() => {
        const defaultValueMap: Record<string, any> = {};
        const emptyValueMap: Record<string, any> = {};
        const rules: Record<string, IRuleDescriptor> = {};
        forEach(memberFieldStore.presetAndCustomFields, (field) => {
            defaultValueMap[field.key] = field.defaultValue;
            emptyValueMap[field.key] = field.emptyValue;
            const rule = getMemberFieldRule(field);
            if (rule) {
                rules[field.key] = rule;
            }
        });
        return [defaultValueMap, emptyValueMap, rules];
    }, []);

    const getFormRules = (): Record<string, IRuleDescriptor> => {
        return {
            name: {
                type: 'string',
                validate: (value: string) => {
                    const length = size(trim(value));
                    if (FIELD_REQUIRED_RULES.nickname) {
                        return length > 0 && length <= MEMBER_NAME;
                    }
                    return length <= MEMBER_NAME;
                },
                message: (value: string) => {
                    if (isEmpty(trim(value))) {
                        return Intl.get('member.invite.name.required');
                    }
                    return '';
                }
            },
            username: {
                type: 'string',
                message: (value: string) => {
                    return size(trim(value)) > MEMBER_ID ? '' : Intl.get('member.id.format');
                },
                validate: (value: string) => {
                    if (isRecoverResignedMember) {
                        return true;
                    }
                    return !value || new RegExp(TEXT_TYPE_REGEXP.USERNAME).test(value);
                }
            },
            phone: {
                type: 'string',
                message: Intl.get('phone.invalid.warning'),
                validate: (value: string) => {
                    return !value || verifyPhone(value, true);
                }
            },
            email: {
                type: 'string',
                message: (value: string) => {
                    if (FIELD_REQUIRED_RULES.email && isEmpty(trim(value))) {
                        return Intl.get('account.signin.email.emptyTip');
                    }
                    if (size(trim(value)) > EMAIL) {
                        return '';
                    }
                    return Intl.get('email.invalid.warning');
                },
                validate: (value: string) => {
                    const isValidEmail = size(trim(value)) <= EMAIL && verifyEmail(value);
                    const isEmptyEmail = isEmpty(value);
                    if (FIELD_REQUIRED_RULES.email) {
                        return !isEmptyEmail && isValidEmail;
                    }
                    return isEmptyEmail || isValidEmail;
                }
            }
        };
    };

    const getFormDefaultValue = () => {
        return {
            name: resignedMember?.nickname || '',
            username: resignedMember?.username || '',
            phone: '',
            email: ''
        };
    };

    const [formDefaultValue, setFormDefaultValue] = useState(getFormDefaultValue);

    const getMoreFormDefaultValue = () => {
        if (isRecoverResignedMember) {
            const valueMap: Record<string, any> = {};
            forEach(defaultValueMap, (value, key) => {
                valueMap[key] = get(resignedMember, ['ext', key], value);
            });
            return valueMap;
        }
        return defaultValueMap;
    };

    const [moreFormDefaultValue, setMoreFormDefaultValue] = useState(getMoreFormDefaultValue);

    // 姓名
    const renderNicknameInput = () => {
        return (
            <Form.Item
                key="name"
                label={Intl.get('name')}
                name="name"
                showRequiredTip={FIELD_REQUIRED_RULES.nickname}
                required={FIELD_REQUIRED_RULES.nickname}
                contentRender={(valid, props) => {
                    const { value } = props;
                    return (
                        <ErrorInput
                            {...props}
                            errMsg={
                                size(trim(value)) > MEMBER_NAME
                                    ? Intl.get('max.length.error.message', { max: MEMBER_NAME })
                                    : ''
                            }
                            inputState={valid ? undefined : 'error'}
                            className="form-item-input"
                            style={{ width: '100%' }}
                            placeholder={
                                FIELD_REQUIRED_RULES.nickname
                                    ? Intl.get('invite.name.placeholder')
                                    : Intl.get('member.name.valid.length')
                            }
                        />
                    );
                }}
            />
        );
    };

    // 编号
    const renderUsernameInput = () => {
        return (
            <Form.Item
                key="username"
                label={Intl.get('serial.number')}
                name="username"
                contentRender={(valid, props) => {
                    const { value } = props;
                    return (
                        <ErrorInput
                            {...props}
                            disabled={isRecoverResignedMember}
                            errMsg={
                                size(trim(value)) > MEMBER_ID
                                    ? Intl.get('max.length.error.message', { max: MEMBER_ID })
                                    : ''
                            }
                            inputState={valid ? undefined : 'error'}
                            className="form-item-input"
                            style={{ width: '100%' }}
                            placeholder={Intl.get('fxp.base.bg.intra_org.intra_id_desc.tip')}
                        />
                    );
                }}
            />
        );
    };

    // 手机
    const renderPhoneInput = () => {
        if (!hasFunctionContactPhone()) {
            return null;
        }
        return (
            <Form.Item key="phone" label={Intl.get('phone')} name="phone">
                <I18nPhone waterMark={Intl.get('invite.phone.or.email.required')} />
            </Form.Item>
        );
    };

    // 邮箱
    const renderEmailInput = () => {
        return (
            <Form.Item
                key="email"
                label={Intl.get('email')}
                name="email"
                showRequiredTip={FIELD_REQUIRED_RULES.email}
                required={FIELD_REQUIRED_RULES.email}
                contentRender={(valid, props) => {
                    const { value } = props;
                    return (
                        <ErrorInput
                            {...props}
                            errMsg={
                                size(trim(value)) > EMAIL
                                    ? Intl.get('max.length.error.message', { max: EMAIL })
                                    : ''
                            }
                            inputState={valid ? undefined : 'error'}
                            className="form-item-input"
                            style={{ width: '100%' }}
                            placeholder={
                                !hasFunctionContactPhone() || FIELD_REQUIRED_RULES.email
                                    ? Intl.get('invite.email.required')
                                    : Intl.get('invite.phone.or.email.required')
                            }
                        />
                    );
                }}
            />
        );
    };

    const renderFormContent = () => {
        return (
            <>
                {map(MANUALLY_INVITE_FIELDS, (item) => {
                    const { field } = item;
                    switch (field) {
                        case 'nickname':
                            return renderNicknameInput();
                        case 'username':
                            return renderUsernameInput();
                        case 'phone':
                            return renderPhoneInput();
                        case 'email':
                            return renderEmailInput();
                        default:
                            return null;
                    }
                })}
            </>
        );
    };
    /**
     * 清空/重置表单
     * @param mode clear表示清空，字段置为空值；reset表示重置，字段置为默认值
     */
    const clearFormValue = (mode: 'clear' | 'reset' = 'clear') => {
        setDeptItems([]);
        setFormDefaultValue({
            ...getFormDefaultValue(),
            name: ''
        });
        setMoreFormDefaultValue(mode === 'clear' ? emptyValueMap : defaultValueMap);
        setRefreshToken((token) => token + 1);
    };

    /**
     * 获取部门Id列表
     *
     * @returns {string[]} 部门Id列表
     */
    const getDeptIdList = () => {
        return map(deptItems, (item) => item._id);
    };

    const dealMemberInvite = () => {
        const { name, username, phone, email } = formRef.current.core.getValue();
        const baseData = {
            username,
            nickname: trim(name),
            phone: phone,
            email: email,
            dept: getDeptIdList()
        };
        const pfData = getMemberFieldCommitValue(
            (key) => memberFieldStore.presetAndCustomFieldMap[key],
            moreFormRef.current?.core.getValue(),
            true
        ); // 预设字段
        const filledFieldList: string[] = [];
        forEach({ ...baseData, ...pfData }, (value, key) => {
            if (!isObjectEmpty(value)) {
                filledFieldList.push(key);
            }
        });
        return post(
            getApi(API.corp.invite, corpUserStore.tenantId),
            {
                ...baseData,
                ext: pfData
            },
            {
                customHandleHttpCodes: [400]
            }
        ).then(
            (res) => {
                const invitationErrCode = get(res, 'data.invitation_err_code');
                if (includes(CONTACTS.INVITE_EXCEED_CODE, invitationErrCode)) {
                    Message.add({
                        messageType: 'toast',
                        type: 'warning',
                        message: getErrorMsg({ code: invitationErrCode })
                    });
                } else {
                    Message.add({
                        messageType: 'toast',
                        type: 'success',
                        message: Intl.get('member.invite.success')
                    });
                    tracker(TRACKER.REEMPLOYMENT_MEMBER_INVITE_SUCCESS);
                }
                clearFormValue('reset');
                onInviteMember();
                tracker(TRACKER.MEMBER_INVITE_DEFAULT_FIELD, join(filledFieldList, ','));
            },
            (error) => {
                const resData = get(error, 'response.data');
                let errMsg = getErrorMsg(resData);
                if (resData.code === ECode.TENANT_FEATURE_REQUIRES_AUTH_APPROVAL) {
                    errMsg = Intl.get('member.invite.not.verified');
                }
                Message.add({
                    messageType: 'toast',
                    type: 'warning',
                    message: errMsg
                });
            }
        );
    };

    const checkMemberInfo = () => {
        const formValue = formRef.current.core.getValue();
        const validateArr = [formRef.current.core.validate()];
        if (moreFormRef.current) {
            validateArr.push(moreFormRef.current.core.validate());
        }
        return Promise.all(validateArr).then(() => {
            const { phone, email, username } = formValue;
            if (!phone && !email) {
                Message.add({
                    messageType: 'toast',
                    type: 'warning',
                    message: hasFunctionContactPhone()
                        ? Intl.get('invite.phone.or.email.required')
                        : Intl.get('invite.email.required')
                });
                return;
            }
            return post(getApi(API.corp.uniq_check, corpUserStore.tenantId), {
                type: MemberType.internal,
                username,
                phone: phone,
                email: email
            }).then(() => {
                return dealMemberInvite();
            });
        });
    };

    /**
     * 部门选择项
     *
     * @returns {React.ReactNode} 部门选择项DOM
     */
    const renderDeptItem = () => {
        return (
            <div className="dept-item">
                <label className="item-label">{Intl.get('department')}</label>
                <div className="item-content">
                    <ContactValuePanel
                        className="dept-value-pane"
                        dataType={DataType.dept}
                        items={deptItems}
                        onClick={() => {
                            setDeptSelectVisible(true);
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderMoreFormContent = () => {
        return map(memberFieldStore.presetAndCustomFields, (field) => {
            switch (field.valueType) {
                case MemberFieldValueType.Text:
                    return (
                        <Form.Item
                            key={field.key}
                            label={field.label}
                            name={field.key}
                            contentRender={(valid, props) => {
                                return (
                                    <ErrorInput
                                        {...props}
                                        errMsg={validateMemberFieldValue(field, props.value)[1]}
                                        inputState={valid ? undefined : 'error'}
                                        className="form-item-input"
                                        style={{ width: '100%' }}
                                        placeholder={Intl.get('fxp.please_enter.tip')}
                                    />
                                );
                            }}
                        />
                    );
                case MemberFieldValueType.Datetime:
                    return (
                        <Form.Item key={field.key} label={field.label} name={field.key}>
                            <MemberFieldDatetime
                                style={{ width: '100%' }}
                                format={field.format || 'yyyy-MM-dd'}
                                placeholder={Intl.get('fxp.select_date.tip')}
                            />
                        </Form.Item>
                    );
                case MemberFieldValueType.Number:
                    return (
                        <Form.Item key={field.key} label={field.label} name={field.key}>
                            <MemberFieldNumber
                                style={{ width: '100%' }}
                                placeholder={Intl.get('fxp.please_enter.tip')}
                            />
                        </Form.Item>
                    );
                case MemberFieldValueType.Dept:
                case MemberFieldValueType.DeptGroup:
                    return (
                        <Form.Item key={field.key} label={field.label} name={field.key}>
                            <MemberFieldDept
                                style={{ width: '100%' }}
                                multiple={field.valueType === MemberFieldValueType.DeptGroup}
                            />
                        </Form.Item>
                    );
                case MemberFieldValueType.User:
                case MemberFieldValueType.UserGroup:
                    return (
                        <Form.Item key={field.key} label={field.label} name={field.key}>
                            <MemberFieldUser
                                style={{ width: '100%' }}
                                multiple={field.valueType === MemberFieldValueType.UserGroup}
                            />
                        </Form.Item>
                    );
                case MemberFieldValueType.Combo:
                case MemberFieldValueType.ComboCheck:
                    return (
                        <Form.Item key={field.key} label={field.label} name={field.key}>
                            <MemberFieldSelect
                                style={{ width: '100%' }}
                                multiple={field.valueType === MemberFieldValueType.ComboCheck}
                                placeholder={Intl.get('fxp.please_select.tip')}
                                options={field.options}
                            />
                        </Form.Item>
                    );
                default:
                    return null;
            }
        });
    };

    return (
        <div className="add-member-panel">
            <div className="invite-field">
                {includes(window.jdy_config.member_invite.show_tips, 'add') && (
                    <div className="manual-tip">
                        <li className="tip-item">{Intl.get('invite.manual.add.tip1')}</li>
                        {hasFunctionContactPhone() && (
                            <li className="tip-item">{Intl.get('invite.manual.add.tip2')}</li>
                        )}
                    </div>
                )}
                <Form
                    key={refreshToken}
                    labelWidth="50"
                    className="member-info-form"
                    ref={formRef}
                    value={formDefaultValue}
                    rules={getFormRules()}
                    validateTrigger="onBlur"
                    layout="vertical"
                >
                    {renderFormContent()}
                </Form>
                {renderDeptItem()}
            </div>
            {showMore ? (
                <div ref={moreContainer}>
                    <Form
                        key={refreshToken}
                        labelWidth="50"
                        className="member-info-more-form"
                        ref={moreFormRef}
                        value={moreFormDefaultValue}
                        rules={moreFormRules}
                        validateTrigger="onBlur"
                        layout="vertical"
                    >
                        {renderMoreFormContent()}
                    </Form>
                </div>
            ) : (
                <Button
                    className="show-more-btn"
                    icon={<DoubleLeftOutlined style={{ rotate: '-90deg' }} />}
                    onClick={() => {
                        setShowMore(true);
                        tracker(TRACKER.MEMBER_INVITE_MORE_INFO);
                    }}
                    type="text"
                    compact
                >
                    {Intl.get('fxp.base.bg.intra_org.fill_more_info.btn')}
                </Button>
            )}
            <DeptSelectDialog
                visible={deptSelectVisible}
                disabledIds={disabledDeptIds}
                footer={
                    <Button
                        type="negative"
                        onClick={() => {
                            openInNewTab(getCorpContactUrl(corpUserStore.tenantId));
                        }}
                    >
                        {Intl.get('contacts')}
                    </Button>
                }
                onOk={(departments) => {
                    setDeptItems(departments);
                    setDeptSelectVisible(false);
                    tracker(TRACKER.MEMBERSELECT_CONTENT, {
                        p1: '',
                        p2: map(departments, '_id').join(','),
                        p3: '',
                        p4: 'pt'
                    });
                }}
                onCancel={() => {
                    setDeptSelectVisible(false);
                }}
                value={deptItems}
                onFetchInternalDepartments={(p) => {
                    return getContactDeptList(corpUserStore.tenantId, { ...p, hasCount: true });
                }}
            />
        </div>
    );
});

export default AddMemberPanel;
